import type { TextElement } from '@/components/canvas/document/publicTypes';
import { TEXT_KNOWN_PLACEHOLDERS } from '@/components/canvas/elements/text/constants';
import {
  detectBlockKind,
  getPlainTextForLayout,
} from '@/components/canvas/elements/text/utils/textContent';
import { adjustBreakForForbiddenLineStart, isForbiddenLineStartChar } from '@/components/canvas/elements/text/utils/cjkLineStart';
import { parseTextLineHeightToPx } from '@/components/canvas/elements/text/utils/textMeasure';
import { getTextLayoutFromElement } from '@/components/canvas/elements/text/model/textStyles';
import { resolveCornerRadiiPx } from '@/components/canvas/shared/utils/geometry/cornerRadius';
import { elementFillCss, firstShadow, firstStroke } from '@/components/canvas/elements/model';
import { roundedRectPath } from '@/components/canvas/rendering/export/roundedRect';

interface TextSegment {
  text: string;
  fontFamily: string;
  fontSize: number;
  fontWeight: number | string;
  fontStyle: string;
  color: string;
  underline?: boolean;
  strikethrough?: boolean;
  superscript?: boolean;
  subscript?: boolean;
  bgColor?: string;
}

type TextStyleBase = {
  fontFamily: string;
  fontSize: number;
  fontWeight: number | string;
  fontStyle: string;
  color: string;
};

function drawTextWithLetterSpacing(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, letterSpacing?: number) {
  if (!letterSpacing) {
    ctx.fillText(text, x, y);
    return;
  }
  let curX = x;
  for (const char of text) {
    ctx.fillText(char, curX, y);
    curX += ctx.measureText(char).width + letterSpacing;
  }
}

function measureTextWidth(ctx: CanvasRenderingContext2D, text: string, letterSpacing?: number): number {
  if (!letterSpacing) return ctx.measureText(text).width;
  let w = 0;
  for (const char of text) w += ctx.measureText(char).width + letterSpacing;
  return w;
}

function segmentFontCss(seg: TextSegment): string {
  return `${seg.fontStyle} ${seg.fontWeight} ${seg.fontSize}px "${seg.fontFamily}", sans-serif`;
}

function measureSegmentWidth(
  ctx: CanvasRenderingContext2D,
  seg: TextSegment,
  letterSpacing?: number
): number {
  ctx.save();
  ctx.font = segmentFontCss(seg);
  const w = measureTextWidth(ctx, seg.text, letterSpacing);
  ctx.restore();
  return w;
}

function drawSegment(
  ctx: CanvasRenderingContext2D,
  seg: TextSegment,
  x: number,
  y: number,
  baseLineHeight: number,
  letterSpacing?: number,
  baseFontSize?: number
): number {
  const fs = baseFontSize ?? seg.fontSize;
  ctx.save();
  ctx.font = segmentFontCss(seg);
  ctx.fillStyle = seg.color;
  ctx.textBaseline = 'top';

  let drawY = y;
  if (seg.superscript) drawY = y - fs * 0.3;
  else if (seg.subscript) drawY = y + fs * 0.5;

  const w = measureTextWidth(ctx, seg.text, letterSpacing);
  if (seg.bgColor) {
    ctx.fillStyle = seg.bgColor;
    ctx.fillRect(x, drawY, w, seg.fontSize * 1.2);
    ctx.fillStyle = seg.color;
  }

  drawTextWithLetterSpacing(ctx, seg.text, x, drawY, letterSpacing);

  if (seg.underline) {
    const underlineY = drawY + seg.fontSize + 1;
    ctx.beginPath();
    ctx.strokeStyle = seg.color;
    ctx.lineWidth = Math.max(1, seg.fontSize / 12);
    ctx.moveTo(x, underlineY);
    ctx.lineTo(x + w, underlineY);
    ctx.stroke();
  }

  if (seg.strikethrough) {
    const strikeY = drawY + seg.fontSize * 0.55;
    ctx.beginPath();
    ctx.strokeStyle = seg.color;
    ctx.lineWidth = Math.max(1, seg.fontSize / 12);
    ctx.moveTo(x, strikeY);
    ctx.lineTo(x + w, strikeY);
    ctx.stroke();
  }

  ctx.restore();
  return x + w;
}

function segmentStyleKey(s: TextSegment): string {
  return [
    s.fontFamily,
    s.fontSize,
    s.fontWeight,
    s.fontStyle,
    s.color,
    s.underline ? 1 : 0,
    s.strikethrough ? 1 : 0,
    s.superscript ? 1 : 0,
    s.subscript ? 1 : 0,
    s.bgColor ?? '',
  ].join('|');
}

function mergeAdjacentSameStyle(segments: TextSegment[]): TextSegment[] {
  const out: TextSegment[] = [];
  for (const seg of segments) {
    if (!seg.text) continue;
    const last = out[out.length - 1];
    if (last && segmentStyleKey(last) === segmentStyleKey(seg)) {
      last.text += seg.text;
    } else {
      out.push({ ...seg });
    }
  }
  return out;
}

/**
 * 与 textMeasure.wrapCanvasPlainText 末尾的 fixCjkLineStartsPullBack 等价，但保留每字样式。
 * 逐段换行时 adjustBreakForForbiddenLineStart 无法覆盖「新 segment 行首」等情形，后处理可避免行首逗号、
 * 并与纯文本行数修正一致，减少「多出一行」。
 */
function fixCjkStyledTextSegmentLines(lines: TextSegment[][]): TextSegment[][] {
  if (lines.length <= 1) return lines;
  type CharCell = { ch: string; seg: TextSegment };
  const flattenLine = (line: TextSegment[]): CharCell[] => {
    const out: CharCell[] = [];
    for (const seg of line) {
      if (!seg.text) continue;
      for (const ch of seg.text) {
        out.push({ ch, seg: { ...seg, text: ch } });
      }
    }
    return out;
  };
  const toSegments = (cells: CharCell[]): TextSegment[] =>
    mergeAdjacentSameStyle(cells.map(({ ch, seg }) => ({ ...seg, text: ch })));

  let charLines = lines.map(flattenLine);
  let changed = true;
  let guard = 0;
  while (changed && guard++ < charLines.length * 8) {
    changed = false;
    for (let i = 1; i < charLines.length; i++) {
      const line = charLines[i];
      if (line.length === 0) continue;
      const c0 = line[0].ch;
      if (!isForbiddenLineStartChar(c0)) continue;
      const prev = charLines[i - 1];
      if (prev.length === 0) continue;
      if (prev.length === 1) {
        charLines[i] = [...prev, ...line];
        charLines[i - 1] = [];
        changed = true;
        continue;
      }
      const last = prev[prev.length - 1];
      charLines[i - 1] = prev.slice(0, -1);
      charLines[i] = [last, ...line];
      changed = true;
    }
  }
  return charLines.filter((cl) => cl.length > 0).map(toSegments);
}

function buildDrawBlocks(el: TextElement, base: TextStyleBase): TextSegment[][] {
  const blocks = el.data.document.blocks.map((block) =>
    mergeAdjacentSameStyle(block.runs.map((run) => ({
      text: run.text,
      fontFamily: run.style?.fontFamily ?? base.fontFamily,
      fontSize: run.style?.fontSize ?? base.fontSize,
      fontWeight: run.style?.fontWeight ?? (run.style?.bold ? 700 : base.fontWeight),
      fontStyle: run.style?.fontStyle ?? (run.style?.italic ? 'italic' : base.fontStyle),
      color: run.style?.color ?? base.color,
      underline: run.style?.underline,
      strikethrough: run.style?.strike,
      superscript: run.style?.superscript,
      subscript: run.style?.subscript,
      bgColor: run.style?.backgroundColor,
    }))),
  );
  if (blocks.length === 0) blocks.push([]);
  return blocks;
}

/** 多样式 segment 按 maxWidth 换行（与 wrapCanvasPlainText 规则一致，含 \n 软换行） */
function wrapStyledLines(
  ctx: CanvasRenderingContext2D,
  segments: TextSegment[],
  maxWidth: number,
  letterSpacing?: number
): TextSegment[][] {
  const lines: TextSegment[][] = [];
  let currentLine: TextSegment[] = [];
  let currentLineW = 0;

  const pushLine = () => {
    if (currentLine.length > 0) {
      lines.push(currentLine);
      currentLine = [];
      currentLineW = 0;
    }
  };

  for (const seg of segments) {
    const parts = seg.text.split('\n');
    for (let pi = 0; pi < parts.length; pi++) {
      if (pi > 0) pushLine();
      let remaining = parts[pi];
      while (remaining.length > 0) {
        ctx.font = segmentFontCss(seg);
        let fitEnd = 0;
        let fitW = 0;
        // 必须用索引 while，禁止在 for..of(remaining) 内改 remaining 后 continue：
        // 迭代器仍绑定旧字符串，会与 slice 后的 remaining 错位，导致缺字、重复或乱行。
        let i = 0;
        while (i < remaining.length) {
          const char = remaining[i]!;
          const charW = ctx.measureText(char).width + (letterSpacing ?? 0);
          if (currentLineW + fitW + charW > maxWidth && (currentLine.length > 0 || fitEnd > 0)) {
            let advance = fitEnd;
            if (fitEnd > 0) {
              let fe = adjustBreakForForbiddenLineStart(remaining, fitEnd);
              if (fe === 0 && fitEnd > 0) fe = fitEnd;
              currentLine.push({ ...seg, text: remaining.slice(0, fe) });
              advance = fe;
            }
            lines.push(currentLine);
            currentLine = [];
            currentLineW = 0;
            remaining = remaining.slice(advance);
            i = 0;
            fitEnd = 0;
            fitW = 0;
            continue;
          }
          fitW += charW;
          fitEnd++;
          i++;
        }
        if (fitEnd > 0) {
          currentLine.push({ ...seg, text: remaining.slice(0, fitEnd) });
          currentLineW += fitW;
        }
        remaining = '';
      }
    }
  }
  if (currentLine.length > 0) lines.push(currentLine);
  if (lines.length === 0) lines.push([]);
  return fixCjkStyledTextSegmentLines(lines);
}

/**
 * vertical-rl：列自右向左，列内自上而下；换列条件为列高用尽；\\n 表示换列。
 * letter-spacing（em）在竖排中与横向一致，取为列间水平间距（近似 CSS）。
 */
function wrapVerticalStyledColumns(
  blockSegments: TextSegment[],
  innerH: number,
  lh: number,
  letterSpacingPx: number | undefined
): TextSegment[][] {
  const maxPerColumn = Math.max(1, Math.floor(innerH / lh));
  const columns: TextSegment[][] = [];
  let currentColumn: TextSegment[] = [];
  let currentColumnChars = 0;

  const pushColumn = () => {
    if (currentColumn.length > 0) {
      columns.push(mergeAdjacentSameStyle(currentColumn));
      currentColumn = [];
      currentColumnChars = 0;
    }
  };

  const pushChar = (seg: TextSegment, ch: string) => {
    if (currentColumnChars >= maxPerColumn && currentColumnChars > 0) {
      pushColumn();
    }
    currentColumn.push({ ...seg, text: ch });
    currentColumnChars++;
  };

  for (const seg of blockSegments) {
    if (!seg.text) continue;
    const parts = seg.text.split('\n');
    for (let pi = 0; pi < parts.length; pi++) {
      if (pi > 0) pushColumn();
      const part = parts[pi];
      for (const ch of part) {
        pushChar(seg, ch);
      }
    }
  }
  pushColumn();
  return columns.length > 0 ? columns : [[]];
}

function columnCharCount(col: TextSegment[]): number {
  let n = 0;
  for (const s of col) n += [...s.text].length;
  return n;
}

/** 竖排一列内水平占位：正文按字宽；标点收窄，避免整段列总宽被标点撑长 */
function measureColumnMaxGlyphWidth(
  ctx: CanvasRenderingContext2D,
  col: TextSegment[],
  letterSpacingPx: number | undefined,
  baseFontSize: number
): number {
  let w = 0;
  for (const seg of col) {
    if (!seg.text) continue;
    for (const ch of seg.text) {
      w = Math.max(w, layoutGlyphWidthForVerticalColumn(ctx, seg, ch, letterSpacingPx, baseFontSize));
    }
  }
  return w;
}

const VERTICAL_PUNCTUATION_MAP: Record<string, string> = {
  '，': '︐',
  '。': '︒',
  '、': '︑',
  '：': '︓',
  '；': '︔',
  '！': '︕',
  '？': '︖',
  '（': '︵',
  '）': '︶',
  '【': '︻',
  '】': '︼',
  '〔': '︹',
  '〕': '︺',
  '《': '︽',
  '》': '︾',
  '〈': '︿',
  '〉': '﹀',
  '「': '﹁',
  '」': '﹂',
  '『': '﹃',
  '』': '﹄',
  '…': '︙',
};

/** 竖排标点：列宽测量与行距压缩，避免 measureText 过宽、整段横向总长被拉长 */
const VERTICAL_PUNCT_LAYOUT_WIDTH_EM = 0.38;
const VERTICAL_PUNCT_LINE_HEIGHT_RATIO = 0.52;

const VERTICAL_PUNCT_OR_MAPPED = new Set<string>([
  ...Object.keys(VERTICAL_PUNCTUATION_MAP),
  ...Object.values(VERTICAL_PUNCTUATION_MAP),
]);

function isVerticalCompressedPunct(ch: string): boolean {
  return VERTICAL_PUNCT_OR_MAPPED.has(ch);
}

function normalizeVerticalGlyph(ch: string): string {
  return VERTICAL_PUNCTUATION_MAP[ch] ?? ch;
}

/** 竖排列内垂直步进：标点略收紧，更贴近相邻文字 */
function verticalGlyphAdvancePx(ch: string, lineHeightPx: number): number {
  return isVerticalCompressedPunct(ch) ? lineHeightPx * VERTICAL_PUNCT_LINE_HEIGHT_RATIO : lineHeightPx;
}

/** 列宽布局用宽度：标点不按全角 em 撑开整列 */
function layoutGlyphWidthForVerticalColumn(
  ctx: CanvasRenderingContext2D,
  seg: TextSegment,
  ch: string,
  letterSpacingPx: number | undefined,
  baseFontSize: number
): number {
  const mapped = normalizeVerticalGlyph(ch);
  ctx.save();
  ctx.font = segmentFontCss(seg);
  const raw = measureTextWidth(ctx, mapped, letterSpacingPx);
  ctx.restore();
  if (isVerticalCompressedPunct(ch) || isVerticalCompressedPunct(mapped)) {
    return Math.min(raw, baseFontSize * VERTICAL_PUNCT_LAYOUT_WIDTH_EM);
  }
  return raw;
}

function columnContentHeightForVertical(col: TextSegment[], lineHeightPx: number): number {
  let h = 0;
  for (const seg of col) {
    if (!seg.text) continue;
    for (const ch of seg.text) {
      h += verticalGlyphAdvancePx(ch, lineHeightPx);
    }
  }
  return h;
}

function isSidewaysVerticalGlyph(ch: string): boolean {
  return /[A-Za-z0-9(){}\[\]<>]/.test(ch);
}

function drawVerticalGlyphSegment(
  ctx: CanvasRenderingContext2D,
  seg: TextSegment,
  ch: string,
  xLeft: number,
  yTop: number,
  lineHeightPx: number,
  letterSpacingPx: number | undefined,
  baseFontSize: number
): void {
  const mapped = normalizeVerticalGlyph(ch);
  const oneCharSeg = { ...seg, text: mapped };
  if (!isSidewaysVerticalGlyph(ch)) {
    drawSegment(ctx, oneCharSeg, xLeft, yTop, lineHeightPx, letterSpacingPx, baseFontSize);
    return;
  }

  ctx.save();
  const boxW = Math.max(seg.fontSize, baseFontSize);
  const cx = xLeft + boxW / 2;
  const cy = yTop + lineHeightPx / 2;
  ctx.translate(cx, cy);
  ctx.rotate(Math.PI / 2);
  drawSegment(
    ctx,
    oneCharSeg,
    -boxW / 2,
    -lineHeightPx / 2,
    lineHeightPx,
    letterSpacingPx,
    baseFontSize
  );
  ctx.restore();
}

async function drawVerticalText(ctx: CanvasRenderingContext2D, el: TextElement) {
  const { x, y } = el.layout;
  const { width, height } = el.size;
  const { typography, paragraph } = getTextLayoutFromElement(el);
  const rawText = el.data.content;
  const fontFamily = typography.fontFamily;
  const fontSize = typography.fontSize;
  const fontWeight = typography.fontWeight;
  const fontStyle = typography.fontStyle ?? 'normal';
  const color = typography.color;
  const letterSpacing = typography.letterSpacing;
  const lineHeight = typography.lineHeight;
  const padding = el.size.padding;
  const stroke = firstStroke(el.appearance);
  const shadow = firstShadow(el.appearance);
  const backgroundColor = elementFillCss(el);
  const borderWidth = stroke?.width ?? 0;
  const borderColor = stroke?.color;
  const borderStyle = stroke?.style;
  const textAlign = paragraph.textAlign ?? 'left';
  const verticalAlign = el.paragraph.verticalAlign ?? 'middle';
  const text = getPlainTextForLayout(el) || rawText;
  if (!text || TEXT_KNOWN_PLACEHOLDERS.has(text)) return;

  const blockType = detectBlockKind(el.data.document);
  const isListMode = blockType === 'unordered-list-item' || blockType === 'ordered-list-item';

  const pt = padding?.top ?? 8;
  const pr = padding?.right ?? 8;
  const pb = padding?.bottom ?? 8;
  const pl = padding?.left ?? 8;
  const radii = resolveCornerRadiiPx(el);

  if (backgroundColor) {
    const path = roundedRectPath(x, y, width, height, radii);
    ctx.fillStyle = backgroundColor;
    ctx.fill(path);
  }

  if (borderWidth && borderWidth > 0 && borderColor) {
    const path = roundedRectPath(x, y, width, height, radii);
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = borderWidth;
    if (borderStyle === 'dashed') ctx.setLineDash([borderWidth * 3, borderWidth * 2]);
    else if (borderStyle === 'dotted') ctx.setLineDash([borderWidth, borderWidth]);
    else ctx.setLineDash([]);
    ctx.stroke(path);
    ctx.setLineDash([]);
  }

  const lh = parseTextLineHeightToPx(lineHeight, fontSize);
  const letterSpacingPx = letterSpacing != null ? Number(letterSpacing) * fontSize : undefined;
  // 竖排导出不额外施加字符间距：避免每个字被人为拉开。
  // （横排仍保留 TextElement 的 letterSpacing 语义）
  const verticalLetterSpacingPx: number | undefined = undefined;
  const bw = Math.max(0, Number(borderWidth) || 0);
  const innerW = Math.max(1, width - pl - pr - 2 * bw);
  const innerH = Math.max(0, height - pt - pb - 2 * bw);
  const contentX0 = x + bw + pl;
  const contentY0 = y + bw + pt;
  const fontCss = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}", sans-serif`;

  ctx.save();
  ctx.beginPath();
  if (radii.tl || radii.tr || radii.br || radii.bl) {
    ctx.clip(roundedRectPath(x, y, width, height, radii));
  } else {
    ctx.rect(x, y, width, height);
    ctx.clip();
  }

  if (shadow) {
    ctx.shadowBlur = shadow.blur;
    ctx.shadowColor = shadow.color;
    ctx.shadowOffsetX = shadow.offsetX;
    ctx.shadowOffsetY = shadow.offsetY;
  }

  ctx.font = fontCss;
  const contentHeight = estimateLineContentHeightPx(ctx, fontSize);
  const halfLeading = Math.max(0, (lh - contentHeight) / 2);

  const baseStyle: TextStyleBase = { fontFamily, fontSize, fontWeight, fontStyle, color };
  const drawBlocks = buildDrawBlocks(el, baseStyle);
  const lineSpacing = isListMode ? 8 : 0;

  type ColumnEntry = { col: TextSegment[]; gapBefore: boolean; isFirstColOfParagraph: boolean; paragraphIndex: number };
  const entries: ColumnEntry[] = [];

  for (let pIdx = 0; pIdx < drawBlocks.length; pIdx++) {
    const blockSegments = drawBlocks[pIdx];
    const cols = wrapVerticalStyledColumns(blockSegments, innerH, lh, letterSpacingPx).filter(
      (col) => columnCharCount(col) > 0
    );
    const gapBefore = pIdx > 0 && lineSpacing > 0;
    for (let c = 0; c < cols.length; c++) {
      entries.push({
        col: cols[c],
        gapBefore: gapBefore && c === 0,
        isFirstColOfParagraph: c === 0,
        paragraphIndex: pIdx,
      });
    }
  }

  if (entries.length === 0) {
    ctx.restore();
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
    return;
  }

  let totalH = 0;
  for (const { col } of entries) {
    totalH = Math.max(totalH, columnContentHeightForVertical(col, lh));
  }

  let contentTopY = contentY0;
  if (verticalAlign === 'middle') contentTopY = contentY0 + (innerH - totalH) / 2;
  else if (verticalAlign === 'bottom') contentTopY = contentY0 + (innerH - totalH);

  let T = 0;
  for (let ei = 0; ei < entries.length; ei++) {
    const { col, gapBefore } = entries[ei];
    if (gapBefore) T += lineSpacing;
    const colW = Math.max(measureColumnMaxGlyphWidth(ctx, col, verticalLetterSpacingPx, fontSize), fontSize * 0.75);
    T += colW;
  }

  let cursorRight: number;
  if (textAlign === 'center') {
    cursorRight = contentX0 + (innerW + T) / 2;
  } else if (textAlign === 'right') {
    cursorRight = contentX0 + innerW;
  } else {
    cursorRight = contentX0 + T;
  }

  for (let ei = 0; ei < entries.length; ei++) {
    const { col, gapBefore, isFirstColOfParagraph, paragraphIndex } = entries[ei];
    if (gapBefore) cursorRight -= lineSpacing;
    const colW = Math.max(measureColumnMaxGlyphWidth(ctx, col, verticalLetterSpacingPx, fontSize), fontSize * 0.75);
    const xLeft = cursorRight - colW;

    if (isListMode && isFirstColOfParagraph) {
      ctx.save();
      ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}", sans-serif`;
      ctx.fillStyle = color;
      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';
      const markerText = blockType === 'ordered-list-item' ? `${paragraphIndex + 1}.` : '•';
      const mw = ctx.measureText(markerText).width;
      const markerX = Math.max(contentX0, xLeft - mw - 4);
      ctx.fillText(markerText, markerX, contentTopY + halfLeading);
      ctx.restore();
    }

    let gy = contentTopY + halfLeading;
    for (const seg of col) {
      if (!seg.text) continue;
      for (const ch of seg.text) {
        drawVerticalGlyphSegment(ctx, seg, ch, xLeft, gy, lh, verticalLetterSpacingPx, fontSize);
        gy += verticalGlyphAdvancePx(ch, lh);
      }
    }
    cursorRight = xLeft;
  }

  ctx.restore();
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
}

function buildTextSegments(
  text: string,
  base: TextStyleBase
): TextSegment[] {
  if (!text) return [];
  return [{ text, ...base }];
}

/** 用字体度量估算行盒内字形高度，避免固定 0.9em 导致上下被 clip 裁掉一部分笔画 */
function estimateLineContentHeightPx(ctx: CanvasRenderingContext2D, fontSize: number): number {
  const m = ctx.measureText('Mg国');
  const a = m.actualBoundingBoxAscent;
  const d = m.actualBoundingBoxDescent;
  if (a != null && d != null && Number.isFinite(a + d) && a + d > 0) {
    return Math.max(fontSize * 0.88, a + d);
  }
  return fontSize * 0.95;
}

/**
 * 两端对齐（与 CSS text-align: justify 类似）：末行不拉伸；仅处理单行单 segment、至少两词。
 * 词间空格均匀分配 extra 宽度；letterSpacing 仍作用于字符级。
 */
function drawJustifiedSingleSegment(
  ctx: CanvasRenderingContext2D,
  seg: TextSegment,
  xLeft: number,
  y: number,
  targetWidth: number,
  letterSpacing: number | undefined,
  baseLineHeight: number,
  baseFontSize: number
): void {
  const trimmed = seg.text.replace(/\s+$/, '');
  const words = trimmed.split(/\s+/).filter(Boolean);
  if (words.length < 2) {
    drawSegment(ctx, seg, xLeft, y, baseLineHeight, letterSpacing, baseFontSize);
    return;
  }
  ctx.save();
  ctx.font = segmentFontCss(seg);
  ctx.fillStyle = seg.color;
  ctx.textBaseline = 'top';
  let wordsW = 0;
  for (const w of words) wordsW += measureTextWidth(ctx, w, letterSpacing);
  const gaps = words.length - 1;
  const spaceBase = measureTextWidth(ctx, ' ', letterSpacing);
  const natural = wordsW + gaps * spaceBase;
  const slack = targetWidth - natural;
  if (slack <= 0.5) {
    ctx.restore();
    drawSegment(ctx, seg, xLeft, y, baseLineHeight, letterSpacing, baseFontSize);
    return;
  }
  const extraPerGap = slack / gaps;
  let curX = xLeft;
  for (let i = 0; i < words.length; i++) {
    drawTextWithLetterSpacing(ctx, words[i], curX, y, letterSpacing);
    curX += measureTextWidth(ctx, words[i], letterSpacing);
    if (i < words.length - 1) curX += spaceBase + extraPerGap;
  }
  ctx.restore();
}

/**
 * 两端对齐（无空格 CJK 或多 segment）：在相邻字符间均分多余宽度，近似 CSS text-align:justify 对中文行的表现。
 * 末行、单行字、或已贴满行宽时不拉伸。
 */
function drawJustifiedInterCharacterSegments(
  ctx: CanvasRenderingContext2D,
  segments: TextSegment[],
  xLeft: number,
  y: number,
  targetWidth: number,
  baseLineHeight: number,
  letterSpacing: number | undefined,
  baseFontSize: number
): void {
  const flat: TextSegment[] = [];
  for (const seg of segments) {
    if (!seg.text) continue;
    for (const ch of seg.text) {
      flat.push({ ...seg, text: ch });
    }
  }
  if (flat.length < 2) {
    let x = xLeft;
    for (const seg of segments) {
      x = drawSegment(ctx, seg, x, y, baseLineHeight, letterSpacing, baseFontSize);
    }
    return;
  }
  let natural = 0;
  for (const seg of flat) {
    ctx.save();
    ctx.font = segmentFontCss(seg);
    natural += measureTextWidth(ctx, seg.text, letterSpacing);
    ctx.restore();
  }
  const slack = targetWidth - natural;
  if (slack <= 0.5) {
    let x = xLeft;
    for (const seg of segments) {
      x = drawSegment(ctx, seg, x, y, baseLineHeight, letterSpacing, baseFontSize);
    }
    return;
  }
  const extraPerGap = slack / (flat.length - 1);
  let curX = xLeft;
  for (let i = 0; i < flat.length; i++) {
    const seg = flat[i]!;
    drawSegment(ctx, seg, curX, y, baseLineHeight, letterSpacing, baseFontSize);
    ctx.save();
    ctx.font = segmentFontCss(seg);
    const w = measureTextWidth(ctx, seg.text, letterSpacing);
    ctx.restore();
    curX += w + (i < flat.length - 1 ? extraPerGap : 0);
  }
}

export async function drawText(ctx: CanvasRenderingContext2D, el: TextElement) {
  const { x, y } = el.layout;
  const { width, height } = el.size;
  const { typography, paragraph } = getTextLayoutFromElement(el);
  const rawText = el.data.content;
  const fontFamily = typography.fontFamily;
  const fontSize = typography.fontSize;
  const fontWeight = typography.fontWeight;
  const fontStyle = typography.fontStyle ?? 'normal';
  const color = typography.color;
  const letterSpacing = typography.letterSpacing;
  const lineHeight = typography.lineHeight;
  const padding = el.size.padding;
  const stroke = firstStroke(el.appearance);
  const shadow = firstShadow(el.appearance);
  const backgroundColor = elementFillCss(el);
  const borderWidth = stroke?.width ?? 0;
  const borderColor = stroke?.color;
  const borderStyle = stroke?.style;
  // 与 TextElement.tsx / TextEditOverlay 一致：未设置时垂直为 middle，水平为 left
  const textAlign = paragraph.textAlign ?? 'left';
  const verticalAlign = el.paragraph.verticalAlign ?? 'middle';

  const text = getPlainTextForLayout(el) || rawText;
  if (!text || TEXT_KNOWN_PLACEHOLDERS.has(text)) return;
  if (getTextLayoutFromElement(el).writingMode === 'vertical-rl') {
    await drawVerticalText(ctx, el);
    return;
  }
  const blockType = detectBlockKind(el.data.document);

  const pt = padding?.top ?? 8;
  const pr = padding?.right ?? 8;
  const pb = padding?.bottom ?? 8;
  const pl = padding?.left ?? 8;
  const radii = resolveCornerRadiiPx(el);

  // ── background ──
  if (backgroundColor) {
    const path = roundedRectPath(x, y, width, height, radii);
    ctx.fillStyle = backgroundColor;
    ctx.fill(path);
  }

  // ── border ──
  if (borderWidth && borderWidth > 0 && borderColor) {
    const path = roundedRectPath(x, y, width, height, radii);
    ctx.strokeStyle = borderColor;
    ctx.lineWidth = borderWidth;
    if (borderStyle === 'dashed') ctx.setLineDash([borderWidth * 3, borderWidth * 2]);
    else if (borderStyle === 'dotted') ctx.setLineDash([borderWidth, borderWidth]);
    else ctx.setLineDash([]);
    ctx.stroke(path);
    ctx.setLineDash([]);
  }

  const lh = parseTextLineHeightToPx(lineHeight, fontSize);
  // TextElement.letterSpacing 语义为 em；Canvas API 需换算为 px
  const letterSpacingPx = letterSpacing != null
    ? Number(letterSpacing) * fontSize
    : undefined;
  const bw = Math.max(0, Number(borderWidth) || 0);
  const innerW = Math.max(1, width - pl - pr - 2 * bw);
  const innerH = Math.max(0, height - pt - pb - 2 * bw);
  const contentX0 = x + bw + pl;
  const contentY0 = y + bw + pt;
  const fontCss = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}", sans-serif`;

  ctx.save();
  // 裁切区与 CSS overflow:hidden 对齐：使用元素完整边界（含 padding），
  // 而非 content 区。若裁切区仅限 content 区，line-height 产生的半行距
  // 会溢出到 padding 内并被截断，导致导出图片中文字上下各被隐藏一小块。
  ctx.beginPath();
  if (radii.tl || radii.tr || radii.br || radii.bl) {
    ctx.clip(roundedRectPath(x, y, width, height, radii));
  } else {
    ctx.rect(x, y, width, height);
    ctx.clip();
  }

  if (shadow) {
    ctx.shadowBlur = shadow.blur;
    ctx.shadowColor = shadow.color;
    ctx.shadowOffsetX = shadow.offsetX;
    ctx.shadowOffsetY = shadow.offsetY;
  }

  ctx.font = fontCss;
  const contentHeight = estimateLineContentHeightPx(ctx, fontSize);
  const halfLeading = Math.max(0, (lh - contentHeight) / 2);

  // ── 构建段落（列表项 or 普通段落）：自研 TextDocument 保留 run 级样式。
  const isListMode = blockType === 'unordered-list-item' || blockType === 'ordered-list-item';

  type StyledLine = { segments: TextSegment[]; markerText?: string; lastInParagraph: boolean };
  const styledLines: StyledLine[] = [];

  const baseStyle: TextStyleBase = { fontFamily, fontSize, fontWeight, fontStyle, color };
  const drawBlocks = buildDrawBlocks(el, baseStyle);

  for (let pIdx = 0; pIdx < drawBlocks.length; pIdx++) {
    const blockSegments = drawBlocks[pIdx];
    const wrappedLines = wrapStyledLines(ctx, blockSegments, innerW, letterSpacingPx);

    let markerText: string | undefined;
    if (isListMode) {
      if (blockType === 'ordered-list-item') markerText = `${pIdx + 1}.`;
      else if (blockType === 'unordered-list-item') markerText = '•';
    }

    wrappedLines.forEach((lineSegs, li) => {
      styledLines.push({
        segments: lineSegs,
        markerText: li === 0 ? markerText : undefined,
        lastInParagraph: li === wrappedLines.length - 1,
      });
    });
  }

  // ── 计算起始 Y ──
  const lineSpacing = isListMode ? 8 : 0;

  // 简化：每行高度相同（lh），段/列表项间加 lineSpacing
  // 统计总高度（每段第一行之后不加 spacing，段间才加）
  let totalH = 0;
  {
    let prevWasParagraphEnd = false;
    styledLines.forEach((sl, i) => {
      if (i > 0) {
        // 判断是否是新段/新列表项的第一行
        const isNewParagraph = sl.markerText !== undefined || (!isListMode && lineSpacing > 0);
        totalH += isNewParagraph && prevWasParagraphEnd ? lineSpacing : 0;
      }
      totalH += lh;
      prevWasParagraphEnd = i === styledLines.length - 1 || styledLines[i + 1]?.markerText !== undefined;
    });
  }

  // 与 DOM flex 对齐：允许 (innerH - totalH) 为负（内容高于容器时仍居中/贴底），不裁成 0，否则单行大字（如「贺」）导出会偏下、视觉上像上移。
  let curY = contentY0;
  if (verticalAlign === 'middle') curY = contentY0 + (innerH - totalH) / 2;
  else if (verticalAlign === 'bottom') curY = contentY0 + (innerH - totalH);

  // ── 逐行绘制 ──
  let markerWidth = 0;
  if (isListMode) {
    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}", sans-serif`;
    markerWidth = ctx.measureText('99. ').width; // 估算符号宽度
  }

  let prevHadMarker = false;
  for (let li = 0; li < styledLines.length; li++) {
    const { segments, markerText, lastInParagraph } = styledLines[li];

    // 段/列表间距（在新段/项开始时插入）
    if (li > 0 && (markerText !== undefined || (!isListMode && lineSpacing > 0 && prevHadMarker))) {
      curY += lineSpacing;
    }

    const lineW = segments.reduce((acc, s) => acc + measureSegmentWidth(ctx, s, letterSpacingPx), 0);
    const textAreaW = isListMode && markerText !== undefined ? innerW - markerWidth : innerW;
    const contentX = contentX0 + (isListMode ? markerWidth : 0);

    let drawX = contentX;
    if (textAlign === 'center') drawX = contentX + textAreaW / 2 - lineW / 2;
    else if (textAlign === 'right') drawX = contentX + textAreaW - lineW;
    // justify：从左缘排满 textAreaW，末行与单行按左对齐

    // 本行文字顶部（行框顶 + half-leading，与 DOM 一致）
    const lineDrawY = curY + halfLeading;

    // 绘制列表符号
    if (markerText !== undefined) {
      ctx.save();
      ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px "${fontFamily}", sans-serif`;
      ctx.fillStyle = color;
      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';
      ctx.fillText(markerText, contentX0, lineDrawY);
      ctx.restore();
    }

    const canJustify =
      textAlign === 'justify' &&
      !lastInParagraph &&
      segments.length === 1 &&
      /\s/.test(segments[0].text) &&
      segments[0].text.trim().split(/\s+/).filter(Boolean).length >= 2 &&
      lineW < textAreaW - 0.5;

    let justifyCharCount = 0;
    for (const s of segments) { justifyCharCount += [...s.text].length; }

    const canJustifyCjk =
      textAlign === 'justify' &&
      !lastInParagraph &&
      !canJustify &&
      justifyCharCount >= 2 &&
      lineW < textAreaW - 0.5;

    if (canJustify) {
      drawJustifiedSingleSegment(
        ctx,
        segments[0],
        contentX,
        lineDrawY,
        textAreaW,
        letterSpacingPx,
        lh,
        fontSize,
      );
    } else if (canJustifyCjk) {
      drawJustifiedInterCharacterSegments(
        ctx,
        segments,
        contentX,
        lineDrawY,
        textAreaW,
        lh,
        letterSpacingPx,
        fontSize,
      );
    } else {
      let segX = textAlign === 'justify' ? contentX : drawX;
      for (const seg of segments) {
        segX = drawSegment(ctx, seg, segX, lineDrawY, lh, letterSpacingPx, fontSize);
      }
    }

    prevHadMarker = markerText !== undefined;
    curY += lh;
  }

  ctx.restore();
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
}

